/**
 * A single row in the notification feed — used by the Inbox and the bell panel.
 *
 * Built for triage rather than reading: severity carries the colour (brand indigo
 * is reserved for selection), the row is one scannable line of title + snippet +
 * meta, and the timestamp sits in tabular monospace so times line up down the
 * column. Roughly half the height of the card it replaces.
 *
 * The Inbox passes `onSelect` and resolves actions in its detail pane. The bell
 * has no detail pane, so it passes `onActionClick` and the actions render inline
 * — outside the clickable region, since a button cannot nest buttons.
 */
import { Button } from 'sharedUi/Button';
import type { Notification, NotificationAction } from '../../types';
import { getRelativeTimeFromUtc } from '../../utils/timezone.util';
import { picturesOf } from './notification-format';
import { severityOf, type Severity } from './notification-severity';
import { isNotificationUnread } from './read-state';

/** Severity rail down the left edge. The single place to change these colours. */
const RAIL: Record<Severity, string> = {
  critical: 'bg-rose-600 dark:bg-rose-500',
  high: 'bg-amber-500 dark:bg-amber-400',
  // A rail on every row reads as a deliberate scale rather than "only some rows
  // are marked". Kept a step lighter than the selection marker so the two do
  // not compete on a selected row.
  normal: 'bg-blue-400 dark:bg-blue-500',
};

const SEV_TAG: Record<Severity, string> = {
  critical: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  high: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  normal: '',
};

const CHANNEL_ICON: Record<string, string> = {
  inApp: 'pi-bell', email: 'pi-envelope', sms: 'pi-comment',
  push: 'pi-mobile', webPush: 'pi-desktop', whatsapp: 'pi-whatsapp',
};

/** Strip template HTML down to a single line of preview text. */
const toSnippet = (html?: string): string =>
  (html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

interface Props {
  notification: Notification;
  selected?: boolean;
  /** Inbox: select the row and show it in the detail pane. */
  onSelect?: (n: Notification) => void;
  /** Bell: no detail pane, so actions render on the row itself. */
  onActionClick?: (n: Notification, a: NotificationAction) => void;
  onMarkAsRead?: (notificationId: string) => void;
}

const NotificationItem = ({
  notification, selected, onSelect, onActionClick, onMarkAsRead,
}: Props) => {
  const sev = severityOf(notification);
  // Shared rule: an item somebody responded to is read even with no read time.
  const isUnread = isNotificationUnread(notification);
  const acted = notification.selectedAction;
  const channels = Object.keys((notification.channels as Record<string, unknown>) || {}).slice(0, 3);
  const openActions = !acted && (notification.availableActions?.length ?? 0) > 0 && !!onActionClick;
  // One picture or several. The row shows the first and says how many more there are;
  // the Inbox is where they are looked at, so it shows them all.
  const pictures = picturesOf(notification);
  const [image] = pictures;

  const activate = () => {
    onSelect?.(notification);
    if (isUnread) onMarkAsRead?.(notification._id);
  };

  return (
    <div
      // Unread rows are highlighted blue — the same cue as the Inbox page — so
      // they stand out in the bell panel too. Selected stays the strongest blue.
      className={`relative border-b border-gray-100 transition-colors dark:border-gray-700/60
        ${selected
          ? 'bg-blue-200 dark:bg-blue-900/70'
          : isUnread
            // Solid blue-100 across the whole row — blue-50 read as white.
            ? 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
      data-testid={`ROW-Notification-${notification._id}`}
    >
      <button
        type="button"
        onClick={activate}
        aria-current={selected || undefined}
        className="grid w-full grid-cols-[3px_1fr_auto] items-start gap-x-3 text-left"
      >
        <span className={`self-stretch ${RAIL[sev]}`} />

        <span className="flex min-w-0 gap-2.5 py-3 pl-0.5">
          {/* A picture the notification carried. A thumbnail rather than the full
              image: the row is one scannable line, and the detail pane is where a
              picture is looked AT. Hidden if it fails to load, so a dead URL costs
              a gap rather than a broken-image icon in the feed. */}
          {image && (
            <span className="relative mt-0.5 shrink-0">
              <img
                src={image}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="h-10 w-10 rounded object-cover ring-1 ring-gray-200 dark:ring-gray-700"
              />
              {pictures.length > 1 && (
                <span className="absolute -bottom-1 -right-1 rounded bg-gray-900/80 px-1 font-mono text-[9px] font-bold text-white">
                  +{pictures.length - 1}
                </span>
              )}
            </span>
          )}

          {/* The notification's own icon, when it carried no picture. A picture says
              more than an icon, so the two never compete for the same slot — and a
              row with neither keeps the layout it has always had. */}
          {!image && typeof notification.icon === 'string' && notification.icon.startsWith('pi ') && (
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-300">
              <i className={`${notification.icon} text-base`} aria-hidden="true" />
            </span>
          )}

          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              {isUnread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600 dark:bg-blue-400" />}
              <span className={`truncate text-[13.5px] ${
                isUnread
                  ? 'font-bold text-blue-700 dark:text-blue-300'
                  : 'font-medium text-ink'}`}>
                {notification.title}
              </span>
            </span>

            <span className="mt-0.5 block truncate text-xs text-ink-muted">
              {toSnippet(notification.body)}
            </span>

            <span className="mt-1.5 flex flex-wrap items-center gap-2">
              {sev !== 'normal' && (
                <span className={`rounded px-1.5 py-px font-mono text-[9.5px] font-bold uppercase tracking-wider ${SEV_TAG[sev]}`}>
                  {sev}
                </span>
              )}
              {acted && (
                <span className="rounded bg-emerald-50 px-1.5 py-px font-mono text-[9.5px] font-bold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                  {acted}
                </span>
              )}
              {channels.length > 0 && (
                <span className="flex items-center gap-1.5 text-ink-muted">
                  {channels.map((c) => (
                    <i key={c} className={`pi ${CHANNEL_ICON[c] || 'pi-circle'} text-[10px]`} />
                  ))}
                </span>
              )}
            </span>
          </span>
        </span>

        <span className="whitespace-nowrap py-3.5 pr-3.5 font-mono text-[11px] tabular-nums text-ink-muted">
          {getRelativeTimeFromUtc(notification.createdDateTime)}
        </span>
      </button>

      {openActions && (
        <div className="flex flex-wrap gap-2 pb-3 pl-4 pr-3.5">
          {notification.availableActions!.map((a) => (
            <Button
              key={a.actionId}
              label={a.label}
              icon={a.icon || undefined}
              size="small"
              variant={a.style === 'primary' ? 'primary' : 'outlined'}
              onClick={() => onActionClick!(notification, a)}
              testId={`BTN-Action-${a.actionId}`}
            />
          ))}
        </div>
      )}

      {acted && notification.memo && (
        <p className="m-0 px-4 pb-3 text-[11.5px] text-ink-muted">
          <span className="font-semibold">Memo:</span> {notification.memo}
        </p>
      )}

      {selected && <span className="absolute inset-y-0 right-0 w-0.5 bg-blue-600 dark:bg-blue-400" />}
    </div>
  );
};

export default NotificationItem;
